import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Folder, Package, FileText } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SEEN_KEY = "ah_whatsnew_seen";

export default function WhatsNewModal() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    api
      .get("/whats-new")
      .then((r) => {
        const since = r.data?.since;
        const total = (r.data?.projects?.length || 0) + (r.data?.assets?.length || 0) + (r.data?.products?.length || 0);
        if (!since || total === 0) return;
        const seen = sessionStorage.getItem(SEEN_KEY);
        // dedupe per session: same `since` value
        if (seen === since) return;
        setData(r.data);
        setLoaded(true);
      })
      .catch(() => {});
  }, [user]);

  function close() {
    if (data?.since) sessionStorage.setItem(SEEN_KEY, data.since);
    setLoaded(false);
    setData(null);
  }

  if (!loaded || !data) return null;

  const sections = [
    {
      key: "projects",
      title: "Preventivi caricati",
      icon: Folder,
      items: data.projects.map((p) => {
        const base = `${p.client_name || "—"} · valore € ${(p.value || 0).toLocaleString("it-IT")}`;
        const secondary = user?.account_type === "trader"
          ? base
          : `${base} · credito + € ${(p.credit_earned || 0).toFixed(2)}`;
        return { id: p.id, primary: p.name, secondary };
      }),
      cta: { to: "/progetti", label: "Vai ai progetti" },
    },
    {
      key: "assets",
      title: "Nuovi asset fornitori",
      icon: FileText,
      items: data.assets.map((a) => ({
        id: a.id,
        primary: a.title,
        secondary: `${a.supplier_name || ""} · ${a.kind}`,
      })),
      cta: { to: "/fornitori", label: "Vai agli asset" },
    },
    {
      key: "products",
      title: "Nuovi prodotti in outlet",
      icon: Package,
      items: data.products.map((p) => ({
        id: p.id,
        primary: p.name,
        secondary: `${p.brand || ""}${p.category ? " · " + p.category : ""}`,
      })),
      cta: { to: "/outlet", label: "Vai all'outlet" },
    },
  ].filter((s) => s.items.length > 0);

  return (
    <div className="fixed inset-0 z-[120] bg-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6" onClick={close} data-testid="whatsnew-modal">
      <div className="bg-cream w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-md md:rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-7 py-5 bg-ink text-cream flex items-center justify-between sticky top-0">
          <div className="flex items-center gap-3">
            <Sparkles size={18} className="text-gold" />
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-cream/60">Bentornato</div>
              <div className="font-display text-lg">Cosa c&apos;è di nuovo</div>
            </div>
          </div>
          <button onClick={close} aria-label="Chiudi" className="w-9 h-9 flex items-center justify-center hover:bg-cream/10 rounded-sm" data-testid="whatsnew-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-7 space-y-6">
          {sections.map((sec) => {
            const Icon = sec.icon;
            return (
              <div key={sec.key} data-testid={`whatsnew-${sec.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon size={16} className="text-ink" />
                    <div className="font-display text-base text-ink">
                      {sec.title} <span className="text-slate1 text-sm">({sec.items.length})</span>
                    </div>
                  </div>
                  <Link to={sec.cta.to} onClick={close} className="text-[11px] tracking-[0.2em] uppercase text-slate1 hover:text-ink underline">
                    {sec.cta.label} →
                  </Link>
                </div>
                <div className="bg-white border border-ink/10 rounded-sm divide-y divide-ink/10">
                  {sec.items.slice(0, 5).map((it) => (
                    <div key={it.id} className="p-3">
                      <div className="text-sm text-ink truncate">{it.primary}</div>
                      <div className="text-xs text-slate1 truncate">{it.secondary}</div>
                    </div>
                  ))}
                  {sec.items.length > 5 && (
                    <div className="p-3 text-xs text-slate1 italic">
                      +{sec.items.length - 5} altri elementi nella sezione dedicata.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-7 py-5 border-t border-ink/10 flex items-center justify-end">
          <button onClick={close} className="bg-ink text-cream px-6 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="whatsnew-dismiss">
            Ho capito
          </button>
        </div>
      </div>
    </div>
  );
}
